import { useEffect, useMemo, useState } from "react";
import {
  useForm,
  useFieldArray,
  useWatch,
  Controller,
  type Control,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X } from "lucide-react";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import {
  PageHeader,
  SaveButton,
  CancelButton,
  ViewButton,
  EditButton,
  DeleteButton,
} from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { PennField, PennInput, PennDropdown, PennText } from "@/components/ui";
import type {
  EditApplicationFormData} from "@/types/admin/application.types";
import {
  editApplicationSchema,
  FASTENER_BRANDS,
} from "@/types/admin/application.types";
import {
  useAllApplications,
  useApplication,
  useDeleteApplication,
  useDeleteCad3dUpload,
  useUpdateApplication,
} from "@/hooks/useApplications";
import { useFastenerCatalog } from "@/hooks/useCatalogs";
import type {
  ApplicationSummary,
  ApplicationFastenerLineInput,
  UpdateApplicationBody,
  FastenerBrandCode,
} from "@/api/applications";

type BrandOption = { value: string; label: string };

type Application = {
  id: string;
  applicationName: string;
  panelPartNumber: string;
  oem: string;
  productionProgram: string;
  /** Owner = the customer the app belongs to (Work-For "Other" email wins). */
  owner: string;
  /** Creator = the staff member who created it (impersonator on registered
   * Work-For; the caller on unregistered "Other"). Equals the owner only for
   * self-created apps — for any Work-For app the two columns differ. */
  creator: string;
  fastenerCount: number;
  has3d: boolean;
  cad3dFileName: string | null;
  cad3dStatus: string | null;
};

type ModalMode = "view" | "edit";

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (
      err as { response?: { data?: { error?: { message?: string } } } }
    ).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

// Map the all-applications summary row to this page's table shape.
// Owner (customer) = working_for_email (unregistered "Other") ?? created_by_email;
// Created by (creator) = created_by_user_id's email ?? created_by_email.
// - Self (no Work-For): both columns match (creator == owner).
// - Registered Work-For: owner = target's email, creator = impersonator's email.
// - Unregistered "Other": owner = working_for_email (customer),
// creator = created_by_email (the staff creator) — the columns differ.
const toRow = (a: ApplicationSummary): Application => ({
  id: a.applicationId,
  applicationName: a.name ?? "",
  panelPartNumber: a.applicationNumber ?? "",
  oem: a.oem ?? "",
  productionProgram: a.productionProgram ?? "",
  owner: a.workingForEmail ?? a.createdByEmail ?? "",
  creator: a.createdByUserEmail ?? a.createdByEmail ?? "",
  fastenerCount: a.fastenerCount,
  has3d: a.has3d,
  cad3dFileName: a.cad3dFileName,
  cad3dStatus: a.cad3dStatus,
});

// One fastener line. Each row watches its own brand and loads that brand's
// type catalog (GET /v1/catalogs/fasteners?brand=&limit=20). OTHER has no
// catalog → the type field falls back to free text.
const FastenerRow = ({
  index,
  control,
  register,
  isView,
  brandOptions,
  onRemove,
}: {
  index: number;
  control: Control<EditApplicationFormData>;
  register: ReturnType<typeof useForm<EditApplicationFormData>>["register"];
  isView: boolean;
  brandOptions: BrandOption[];
  onRemove: () => void;
}) => {
  const brand = useWatch({ control, name: `fasteners.${index}.brand` }) as
    | FastenerBrandCode
    | undefined;
  const { data: catalog = [] } = useFastenerCatalog(brand);
  const isOther = brand === "OTHER";

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-center">
      <Controller
        name={`fasteners.${index}.brand`}
        control={control}
        render={({ field }) => (
          <PennDropdown
            inputId={`brand-${index}`}
            name={field.name}
            options={brandOptions}
            value={field.value}
            onChange={(v) => field.onChange(v)}
            onBlur={field.onBlur}
            isDisabled={isView}
            isSearchable
          />
        )}
      />
      <Controller
        name={`fasteners.${index}.type`}
        control={control}
        render={({ field }) => {
          if (isOther) {
            // No catalog for OTHER — free text input.
            return (
              <PennInput
                disabled={isView}
                placeholder="Free text"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            );
          }
          const opts: BrandOption[] = catalog.map((c) => ({
            value: c.typeCode,
            label: c.typeDisplay,
          }));
          // Keep the saved value visible even if it's outside the top-20.
          if (field.value && !opts.some((o) => o.value === field.value)) {
            opts.unshift({ value: field.value, label: field.value });
          }
          return (
            <PennDropdown
              inputId={`type-${index}`}
              name={field.name}
              options={opts}
              value={field.value ?? ""}
              onChange={(v) => field.onChange(v)}
              onBlur={field.onBlur}
              isDisabled={isView}
              isSearchable
              placeholder="Type to search…"
            />
          );
        }}
      />
      <PennInput
        type="number"
        disabled={isView}
        {...register(`fasteners.${index}.qtyPerPartA`)}
      />
      <PennInput
        type="number"
        disabled={isView}
        {...register(`fasteners.${index}.qtyPerPartB`)}
      />
      {!isView ? (
        <button
          type="button"
          onClick={onRemove}
          className="flex items-center justify-center w-9 h-9 rounded-lg border border-red-200 dark:border-red-800 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors cursor-pointer"
          aria-label="Remove fastener"
        >
          <X size={15} />
        </button>
      ) : (
        <span />
      )}
    </div>
  );
};

export const ApplicationsPage = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPageIndex(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset: pageIndex * pageSize,
      ...(search ? { search } : {}),
    }),
    [pageIndex, pageSize, search]
  );

  const { data, isLoading, isFetching, isError, error } =
    useAllApplications(params);
  const apps = useMemo<Application[]>(
    () => (data?.items ?? []).map(toRow),
    [data]
  );
  const total = data?.total ?? 0;
  const hasNext = (pageIndex + 1) * pageSize < total;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>("edit");
  const [deleteTarget, setDeleteTarget] = useState<Application | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The selected row carries the 3D filename/status from the list (/all).
  const activeApp = useMemo(
    () => apps.find((a) => a.id === activeId) ?? null,
    [apps, activeId]
  );

  const detail = useApplication(activeId);
  const updateMutation = useUpdateApplication();
  const deleteMutation = useDeleteApplication();
  const deleteCad3d = useDeleteCad3dUpload();
  const isView = modalMode === "view";

  // Soft-delete the application (DELETE /v1/applications/:id). The confirm modal
  // closes itself on confirm, so a failure is surfaced as a banner above the table.
  const onConfirmDelete = () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteError(null);
    deleteMutation.mutate(id, {
      onError: (e) =>
        setDeleteError(apiErrorMessage(e, "Failed to delete the application.")),
    });
  };

  // Delete the current 3D upload. Allowed regardless of analysis status so a
  // stuck/worker-down "queued" upload can still be cleared.
  const onDeleteCad3d = () => {
    if (!activeId) return;
    if (
      !window.confirm(
        "Delete the uploaded 3D model? This removes the STEP file and its preview, plus the related library entries.",
      )
    ) {
      return;
    }
    setFormError(null);
    deleteCad3d.mutate(activeId, {
      onError: (e) =>
        setFormError(apiErrorMessage(e, "Failed to delete the 3D model.")),
    });
  };

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<EditApplicationFormData>({
    resolver: zodResolver(editApplicationSchema),
    defaultValues: { fasteners: [] },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "fasteners",
  });

  // Application-editor brand options. The application form exposes all FIVE
  // brands (incl. Autoriv) — POC parity — NOT the
  // 4-chip customer filter-bar set returned by `useFastenerBrands()` (that
  // API deliberately omits Autoriv). So we drive the editor from the local
  // 5-brand list with explicit labels.
  const brandOptions: BrandOption[] = useMemo(
    () =>
      FASTENER_BRANDS.map((b) => ({
        value: b,
        label: b === "OTHER" ? "Other" : b === "AUTORIV" ? "Autoriv" : b,
      })),
    []
  );

  // Populate the form once the detail (with fastener lines) arrives.
  useEffect(() => {
    const d = detail.data;
    if (!d) return;
    reset({
      applicationName: d.name ?? "",
      panelPartNumber: d.applicationNumber ?? "",
      oem: d.oem ?? "",
      productionProgram: d.productionProgram ?? "",
      material: d.materialCode ?? "",
      annualProductionVolume: d.annualVolume ?? 0,
      averageBatchSize: d.batchSize ?? undefined,
      createdBy: d.createdByEmail ?? "",
      fasteners: d.fasteners.map((f) => ({
        fastenerId: f.fastenerId,
        brand: (f.brand ?? "PEM") as (typeof FASTENER_BRANDS)[number],
        type: f.type ?? "",
        qtyPerPartA: f.qtyPerPartA ?? f.qtyPerPart ?? 0,
        qtyPerPartB: f.qtyPerPartB ?? 0,
      })),
    });
  }, [detail.data, reset]);

  const openView = (app: Application) => {
    setFormError(null);
    setModalMode("view");
    setActiveId(app.id);
  };
  const openEdit = (app: Application) => {
    setFormError(null);
    setModalMode("edit");
    setActiveId(app.id);
  };
  const closeModal = () => {
    setActiveId(null);
    reset({ fasteners: [] });
  };

  const onSubmit = (formData: EditApplicationFormData) => {
    if (!activeId) return;
    setFormError(null);

    // Map UI rows to the backend qty XOR rule: both A+B → A/B path; a single
    // side → qtyPerPart; no qty → skip the line. All qty values must be > 0.
    const fasteners: ApplicationFastenerLineInput[] = [];
    for (const f of formData.fasteners) {
      const a = f.qtyPerPartA && f.qtyPerPartA > 0 ? f.qtyPerPartA : null;
      const b = f.qtyPerPartB && f.qtyPerPartB > 0 ? f.qtyPerPartB : null;
      const type = f.type && f.type.trim() ? f.type.trim() : null;
      if (a !== null && b !== null) {
        fasteners.push({
          brand: f.brand,
          type,
          qtyPerPartA: a,
          qtyPerPartB: b,
        });
      } else if (a !== null) {
        fasteners.push({ brand: f.brand, type, qtyPerPart: a });
      } else if (b !== null) {
        fasteners.push({ brand: f.brand, type, qtyPerPart: b });
      }
    }

    const body: UpdateApplicationBody = {
      name: formData.applicationName.trim(),
      applicationNumber: formData.panelPartNumber.trim() || null,
      oem: formData.oem.trim() || null,
      productionProgram: formData.productionProgram?.trim() || null,
      materialCode: formData.material?.trim() || null,
      annualVolume: formData.annualProductionVolume ?? null,
      batchSize: formData.averageBatchSize ?? null,
      fasteners,
    };

    updateMutation.mutate(
      { id: activeId, body },
      {
        onSuccess: () => closeModal(),
        onError: (err) =>
          setFormError(apiErrorMessage(err, "Failed to save application.")),
      }
    );
  };

  const cols: ColDef<Application>[] = [
    {
      header: "ID",
      render: (a) => <span className="text-blue_grey4 text-small">{a.id}</span>,
    },
    { header: "Application", key: "applicationName" },
    { header: "Number", key: "panelPartNumber" },
    { header: "OEM", key: "oem" },
    { header: "Program", key: "productionProgram" },
    { header: "Owner", key: "owner" },
    { header: "Created by", key: "creator" },
    {
      header: "Fasteners",
      render: (a) => (
        <span className="inline-flex items-center justify-center min-w-7 h-7 px-2 rounded-full bg-penn-50 dark:bg-penn-900/40 text-penn-500 dark:text-penn-400 border border-penn-100 dark:border-penn-700 text-small-medium">
          {a.fastenerCount}
        </span>
      ),
    },
    {
      header: "3D ",
      render: (a) => {
        if (!a.has3d && !a.cad3dFileName)
          return(<>
          
          <span className="text-blue_grey4 text-small">Status : Not Applicable</span>
          {/* <p className="mt-2 text-blue_grey4 text-small">
                  Delete 3D removes uploaded STEP files and related library
                  entries.
                </p> */}
          </>) 
        ;
        return (
          <div className="flex flex-col gap-1 max-w-50">
            {a.cad3dFileName && (
              <span
                className="text-blue_grey2 truncate text-small"
                title={a.cad3dFileName}
              >
                {a.cad3dFileName}
              </span>
            )}
            {a.cad3dStatus && (
              <>
                <span className="text-blue_grey4 text-small">Status : { a.cad3dStatus === "ready"?"Ready":""} {a.cad3dStatus === "pending"?"Pending":""}</span>
                {/* <span
                  className={`inline-flex w-fit items-center gap-2 px-2 py-0.5 text-small-medium rounded-full border ${
                    ready
                      ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400"
                      : "border-gray-200 bg-gray-100 text-blue_grey3 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${ready ? "bg-green-500" : "bg-gray-400"}`}
                  />
                  {a.cad3dStatus}
                </span> */}

              </>
            )}

{/* <p className="mt-2 text-blue_grey4 text-small">
                  Delete 3D removes uploaded STEP files and related library
                  entries.
                </p> */}
          </div>
        );
      },
    },
    {
      header: "Actions",
      render: (a) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => openView(a)} />
          <EditButton onClick={() => openEdit(a)} />
          <DeleteButton onClick={() => setDeleteTarget(a)} />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Applications"
        subtitle="Edit/delete saved applications."
      />

      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="w-full max-w-sm">
          <PennInput
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by owner name…"
          />
        </div>
      </div>

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-red-700 dark:text-red-400 text-body">
          Failed to load applications
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-red-700 dark:text-red-400 text-body">
          {deleteError}
        </div>
      )}

      <AdminTable
        columns={cols}
        rows={apps}
        keyExtractor={(a) => a.id}
        loading={isLoading}
        emptyMessage={search ? `No applications found for owner "${search}".` : "No applications found."}
      />

      <p className="mt-2 text-blue_grey4 text-small">
        Delete 3D removes uploaded STEP files and related library entries. Cannot be undone
      </p>

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={apps.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />

      <AdminModal
        open={activeId !== null}
        onClose={closeModal}
        title={isView ? "View application" : "Edit application"}
        maxWidth="max-w-3xl"
        footer={
          isView ? (
            <CancelButton onClick={closeModal} />
          ) : (
            <>
              <CancelButton onClick={closeModal} />
              <SaveButton
                onClick={handleSubmit(onSubmit)}
                label={updateMutation.isPending ? "Saving…" : "Save changes"}
              />
            </>
          )
        }
      >
        {detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">
            Loading application…
          </div>
        ) : detail.isError ? (
          <div className="py-10 text-center text-red-600 text-body">
            Failed to load application.
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {formError && (
              <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-red-700 dark:text-red-400 text-body">
                {formError}
              </div>
            )}

            <PennText
              as="p"
              size="xs"
              font="bold"
              color="blue_grey"
              className="uppercase pb-1"
            >
              Part Information
            </PennText>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PennField
                label="Application / Panel name"
                htmlFor="applicationName"
                required={!isView}
                error={!isView ? errors.applicationName?.message : undefined}
              >
                <PennInput
                  id="applicationName"
                  disabled={isView}
                  error={!isView && !!errors.applicationName}
                  {...register("applicationName")}
                />
              </PennField>
              <PennField
                label="Panel Part Number"
                htmlFor="panelPartNumber"
                required={!isView}
                error={!isView ? errors.panelPartNumber?.message : undefined}
              >
                <PennInput
                  id="panelPartNumber"
                  disabled={isView}
                  error={!isView && !!errors.panelPartNumber}
                  {...register("panelPartNumber")}
                />
              </PennField>
              <PennField
                label="OEM"
                htmlFor="oem"
                required={!isView}
                error={!isView ? errors.oem?.message : undefined}
              >
                <PennInput
                  id="oem"
                  disabled={isView}
                  error={!isView && !!errors.oem}
                  {...register("oem")}
                />
              </PennField>
              <PennField
                label="Production program"
                htmlFor="productionProgram"
                error={!isView ? errors.productionProgram?.message : undefined}
              >
                <PennInput
                  id="productionProgram"
                  disabled={isView}
                  {...register("productionProgram")}
                />
              </PennField>
              <PennField
                label="Material"
                htmlFor="material"
                error={!isView ? errors.material?.message : undefined}
              >
                <PennInput
                  id="material"
                  maxLength={100}
                  disabled={isView}
                  error={!isView && !!errors.material}
                  {...register("material")}
                />
              </PennField>
              <PennField
                label="Annual production volume"
                htmlFor="annualProductionVolume"
                error={
                  !isView ? errors.annualProductionVolume?.message : undefined
                }
              >
                <PennInput
                  id="annualProductionVolume"
                  type="number"
                  disabled={isView}
                  error={!isView && !!errors.annualProductionVolume}
                  {...register("annualProductionVolume")}
                />
              </PennField>
              <PennField
                label="Average batch size (parts/batch)"
                htmlFor="averageBatchSize"
                error={!isView ? errors.averageBatchSize?.message : undefined}
              >
                <PennInput
                  id="averageBatchSize"
                  type="number"
                  disabled={isView}
                  error={!isView && !!errors.averageBatchSize}
                  {...register("averageBatchSize")}
                />
              </PennField>
            </div>
            <PennField label="Created by (email)" htmlFor="createdBy">
              <PennInput
                id="createdBy"
                type="email"
                disabled
                readOnly
                {...register("createdBy")}
              />
            </PennField>

            {/* 3D part */}
            <div className="pt-2">
              <PennText
                as="p"
                size="xs"
                font="bold"
                color="blue_grey"
                className="uppercase"
              >
                3D
              </PennText>
              <div className="mt-2 flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-3 bg-gray-50/50 dark:bg-gray-800/30">
                <span className="text-blue_grey truncate text-body">
                  {activeApp?.cad3dFileName ?? "No 3D model uploaded"}
                </span>
                {activeApp?.cad3dStatus && (
                  <span
                    className={`inline-flex w-fit items-center gap-2 px-2 py-0.5 text-small-medium rounded-full border ${
                      activeApp.cad3dStatus === "ready"
                        ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400"
                        : "border-gray-200 bg-gray-100 text-blue_grey3 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${activeApp.cad3dStatus === "ready" ? "bg-green-500" : "bg-gray-400"}`}
                    />
                    {activeApp.cad3dStatus}
                  </span>
                )}
                {!isView && activeApp?.cad3dFileName && (
                  <button
                    type="button"
                    onClick={onDeleteCad3d}
                    disabled={deleteCad3d.isPending}
                    className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/40 text-small-medium"
                  >
                    <X size={14} />
                    {deleteCad3d.isPending ? "Deleting…" : "Delete"}
                  </button>
                )}
              </div>
              <PennText as="p" size="xs" color="blue_grey" className="mt-2">
                Delete 3D removes uploaded STEP files and related library
                entries. Cannot be undone
              </PennText>
            </div>

            {/* Fastener Information */}
            <div className="flex items-start justify-between pt-2">
              <div>
                <PennText
                  as="p"
                  size="xs"
                  font="bold"
                  color="blue_grey"
                  className="uppercase"
                >
                  Fastener Information
                </PennText>
                <PennText as="p" size="xs" color="blue_grey" className="mt-1">
                  Define the fasteners installed on this part.
                </PennText>
              </div>
              {!isView && (
                <button
                  type="button"
                  onClick={() =>
                    append({
                      brand: "PEM",
                      type: "",
                      qtyPerPartA: 0,
                      qtyPerPartB: 0,
                    })
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 bg-penn-500 hover:bg-penn-700 text-white rounded-lg shadow-sm transition-all duration-150 cursor-pointer text-body-medium"
                >
                  <Plus size={15} /> Add fastener
                </button>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-gray-50/50 dark:bg-gray-800/30 overflow-x-auto">
              {fields.length === 0 ? (
                <p className="text-blue_grey4 py-4 text-center text-body">
                  No fasteners added yet.
                </p>
              ) : (
                <div className="space-y-3 min-w-160">
                  {/* Column headers */}
                  <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 text-blue_grey3 text-small-medium">
                    <span>Brand</span>
                    <span>Fastener type</span>
                    <span>Qty / part (A-side)</span>
                    <span>Qty / part (B-side)</span>
                    <span />
                  </div>
                  {fields.map((field, index) => (
                    <FastenerRow
                      key={field.id}
                      index={index}
                      control={control}
                      register={register}
                      isView={isView}
                      brandOptions={brandOptions}
                      onRemove={() => remove(index)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Attachments (general "Upload files", svc-06 /v1/applications/:id/files).
                View mode is download-only; edit mode allows upload + delete. */}
            {/* {activeId && (
              <div className="pt-2">
                <PennText
                  as="p"
                  size="xs"
                  font="bold"
                  color="blue_grey"
                  className="uppercase pb-2"
                >
                  Attachments
                </PennText>
                <AttachmentsPanel applicationId={activeId} readOnly={isView} />
              </div>
            )} */}
          </form>
        )}
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={onConfirmDelete}
        itemLabel={deleteTarget?.applicationName}
      />
    </>
  );
};
